import type { CapabilitySelectDto, MissionAssessmentCapabilityAiGenerationDataDto } from '@@types/api.types';
import { CapabilityDefinitionTooltip } from '@components/shared/capability-definition.component';
import { ModelDisplay } from '@components/shared/form-suggestion.component';
import { Badge } from '@components/ui/badge';
import { Button } from '@components/ui/button';
import { cn } from '@lib/utils';
import { CheckIcon, XIcon } from 'lucide-react';
import type { FC } from 'react';
import { getCapabilitySuggestionItems } from './mission-assessment-capability-suggestion.util';

type MissionAssessmentCapabilitySuggestionProps = {
  capabilities: CapabilitySelectDto[];
  className?: string;
  data: MissionAssessmentCapabilityAiGenerationDataDto | null;
  isGenerating?: boolean;
  model?: string;
  onAccept: () => void;
  onReject: () => void;
};

type MissionAssessmentCapabilitySuggestionBadgesProps = {
  capabilities: CapabilitySelectDto[];
  data: MissionAssessmentCapabilityAiGenerationDataDto;
};

/**
 * Tone-neutral capability badge list shared by the inline suggestion card and
 * the fullscreen chat artifact panel.
 */
export const MissionAssessmentCapabilitySuggestionBadges: FC<MissionAssessmentCapabilitySuggestionBadgesProps> = (
  props,
) => {
  const { capabilities, data } = props;
  const items = getCapabilitySuggestionItems({ capabilities, suggestion: data });

  return (
    <div className="flex flex-wrap gap-2">
      {items.map((item) => (
        <Badge
          className="border-transparent bg-gray-200 text-gray-700 hover:bg-gray-200"
          key={item.id}
        >
          <span>{item.name}</span>
          <CapabilityDefinitionTooltip definition={item.description} />
        </Badge>
      ))}
    </div>
  );
};

export const MissionAssessmentCapabilitySuggestion: FC<MissionAssessmentCapabilitySuggestionProps> = (props) => {
  const { capabilities, className, data, isGenerating = false, model, onAccept, onReject } = props;

  if (!data) {
    return null;
  }

  return (
    <div className={cn('mt-2 rounded-lg border border-blue-300 bg-blue-50 p-4', className)}>
      <MissionAssessmentCapabilitySuggestionBadges
        capabilities={capabilities}
        data={data}
      />

      <div className="mt-6 flex items-center justify-between gap-3 border-t pt-4">
        <div className="text-xs font-medium text-gray-500">
          <ModelDisplay model={model} />
        </div>
        {!isGenerating && (
          <div className="flex items-center gap-3">
            <Button
              onClick={onReject}
              size="sm"
              type="button"
              variant="destructive"
            >
              <XIcon className="size-4" />
              Reject
            </Button>
            <Button
              onClick={onAccept}
              size="sm"
              type="button"
              variant="primary"
            >
              <CheckIcon className="size-4" />
              Accept
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};
